<template>
  <div :id="module && module.id">
    <CRFormRenderer
      v-if="formConfig"
      :module="module"
      :form-config="formConfig"
      :job-data="job"
      :is-submitting="isSubmitting"
      :global-error="genericBackendError"
      :is-submit-successful="submitSuccess && !isNavigating"
      :external-field-errors="externalFieldErrors"
      @submit="onSubmit"
    />
  </div>
</template>

<script setup lang="ts">
import type { PropType } from "vue"
import { nextTick } from "vue"
import { useJobRouteParams } from "~/composables/useJobRouteParams"
import type { ApplicationType } from "~/utils/enums/ApplicationType"

const props = defineProps({
  module: {
    type: Object as PropType<ContentModule>,
    required: true,
  },
  job: {
    type: Object as PropType<FlatJob>,
    required: false,
  },
})

// Capture route params before first await — useRoute() relies on getCurrentInstance()
const { jobId } = useJobRouteParams()
const effectiveJobId = jobId || null

const { data: jobShop } = useNuxtData("jobShopData")
const customPath = useCustomPath()

// Fetch the form configuration, injecting the JobShop locale for datepicker localisation and
// the JobShop itself for the palette the form colours resolve against
const { formConfig } = await useFormConfig(effectiveJobId, props.job?.group_id ?? null, { locale: jobShop.value?.locale, jobShop: jobShop.value })

// Handle form submission
const { handleSubmit, isSubmitting, submitSuccess, externalFieldErrors, genericBackendError } = useApplicationFormData(
  formConfig.value,
  effectiveJobId,
  props.job?.ats_application_customer_name ?? null,
  props.job?.external_id ?? null,
  props.job?.external_reference_id ?? null,
  (props.job?.application_type as ApplicationType) ?? null,
)

const isNavigating = ref(false)

watch(submitSuccess, (val) => {
  if (val) {
    isNavigating.value = true
    if (import.meta.client)
      sessionStorage.setItem("applicationSubmitted", "1")
    navigateTo(
      {
        path: `${(customPath.value ?? "").replace(/\/$/, "")}/success`,
        query: {
          jobId: effectiveJobId ?? undefined,
        },
      },
      { replace: true },
    )
  }
})

async function onSubmit(payload: { valid: boolean, values: Record<string, any>, errors: Record<string, any> }) {
  await handleSubmit(payload)

  if (!import.meta.client)
    return

  await nextTick()

  const root = document.getElementById(props.module?.id ?? "")
  if (!root)
    return

  const errorSelector = ".global-error-message, .field-error, .file-error-message"
  const targetElement
    = root.querySelector<HTMLElement>(errorSelector)?.closest<HTMLElement>(".grid-item")
    ?? root.querySelector<HTMLElement>(errorSelector)

  if (!targetElement)
    return

  targetElement.scrollIntoView({ behavior: "smooth", block: "center" })
}
</script>

<style scoped lang="scss">
.cr-form-renderer-container {
  width: 100%;
}
</style>
